















import moment from 'moment'
import _chunk from 'lodash/chunk'
import { computed, toRefs, defineComponent, PropType, Ref, ComputedRef, onMounted } from '@vue/composition-api'
import useDynamicForm from '@/components/DynamicForms/useDynamicForm'
import useFeatureFlags from '@/components/composables/useFeatureFlags'
import { INTEGRATION_ETHOS_INSTRUCTIONAL_METHOD_ID_KEY } from '@shared/constants/featureFlags'
import { humanizeMeetingPattern } from '@shared/sm/sections/subdomains/meetingPatterns/utils'
import FormText from '@/components/Forms/FormText.vue'
import dateTimeUtils from '@shared/utils/dateTime'
import { Question, QuestionID, Questions } from '@shared/sm/general/sectionTemplate/subdomains/questions/types'
import { InstructionalMethod } from '@shared/instructionalMethods/types'
import { Section } from '@shared/sm/sections/types'
import { Term } from '@shared/sm/general/terms/types'
import { MeetingPattern } from '@shared/sm/sections/subdomains/meetingPatterns/types'
import useVuelidate from '@vuelidate/core'
import useTerm from '@sm/client/components/SchedulingManagement/general/terms/useTerm'
import useDataSources from '@/components/DynamicForms/useDataSources'

export default defineComponent({
  components: {
    FormText,
    DynamicForm: () => import('@/components/DynamicForms/DynamicForm.vue'),
  },
  props: {
    section: {
      type: Object as PropType<Section>,
      required: true,
    },
    term: {
      type: Object as PropType<Term>,
      required: true,
    },
    questions: {
      type: Object as PropType<Questions>,
      required: true,
    },
    index: {
      type: Number,
      required: true,
    },
    editable: {
      type: Boolean,
      default: true,
    },
  },

  setup (props, { root, emit }) {
    const { integrationKeyIMByID } = useFeatureFlags({
      integrationKeyIMByID: INTEGRATION_ETHOS_INSTRUCTIONAL_METHOD_ID_KEY,
    })

    const {
      section,
      term,
      questions,
      index,
    } = toRefs(props)

    const { getTermReferenceValue } = useTerm({ term: term.value })

    // 1. We need to use path here, in order for Actions to work.
    // Otherwise DynamicForm will NOT get formValue (section),
    // so it won't be able to trigger actions based on Section values.
    // 2. time can be undefined here, because then user removes given time,
    // reactivity will trigger here immediatley, even before
    // MeetingPatternsDetailsModal re-renders times after removal.
    const time: ComputedRef<MeetingPattern | undefined> = computed(() => section.value.times[index.value])
    const path = computed(() => `times.${index.value}`)
    const meetingPatternLabel: ComputedRef<string> = computed(() => time.value
      ? humanizeMeetingPattern(time.value)
      : '[Meeting Pattern not found]'
    )

    const { addLocalSourceData } = useDataSources({ overrideLocalSources: true })
    addLocalSourceData('record', time)

    function mapQuestionConfig (question: Question, questionId: QuestionID) {
      switch (questionId) {
        case 'times.startDate':
          return {
            ...question,
            ...getTermReferenceValue({
              question,
              partOfTermId: section.value.partOfTerm,
            }),
            __forceComputed: true,
          }
        case 'times.endDate':
          return {
            ...question,
            ...getTermReferenceValue({
              question,
              partOfTermId: section.value.partOfTerm,
            }),
            config: time.value?.startDate
              ? { disabledDates: { to: moment(time.value.startDate).toDate() } }
              : {},
            __forceComputed: true,
          }
        // This is a hack to make Actions work for Room field.
        // The actual field is displayed in MeetingPatternsTable.
        case 'times.room':
          return {
            ...question,
            rolesAllowedToSee: [],
          }
        case 'times.instructionalMethod':
          // if INTEGRATION_ETHOS_INSTRUCTIONAL_METHOD_ID_KEY FF is ON,
          // we return a config object that does the following
          // 1. Allows us to dynakmically fetch select options from integration attribute mappings
          // 2. Allows us to filter those options by the instructional methods already set on the section
          if (integrationKeyIMByID) {
            // options are just array of IDs
            const imOptions = (section.value.instructionalMethods || [])
              .map(im => im.id)
              .filter(Boolean)

            return {
              ...question,
              __forceComputed: true,
              config: {
                multiple: false,
                options: imOptions,
                dynamicOptions: {
                  enabled: true,
                  persistValue: 'code',
                  displayValue: 'description',
                  attributeType: 'sections',
                  attributeFieldName: 'instructionalMethods',
                  // the allowedOptions field allows us to restrict with attribute options
                  // get displayed for this select
                  useHardcodedOptionsOverride: true,
                },
              },
            }
          }
          const imOptions = (section.value.instructionalMethods || [])
            .map((im: InstructionalMethod, imIndex: number): InstructionalMethod => ({
              ...im,
              // Just for the Ux purpose, if `code` is missing
              // (e.g because user just added empty IM),
              // we display fallback to order in Array.
              ...!im.code && { code: `Instructional Method #${imIndex + 1}` },
            }))
          return {
            ...question,
            __forceComputed: true,
            dataTest: question.label,
            config: {
              multiple: false,
              trackBy: 'id',
              label: 'code',
              options: imOptions,
            },
          }
        default:
          return question
      }
    }

    function customFormUpdateImpl ({ property, value }: {
      property: string,
      value: any,
    }): void {
      // Because DynamicForm uses path here,
      // property is e.g 'times.0.startDate'.
      const dataKey = property.split('.').slice(-1)[0] as keyof MeetingPattern
      // time can be possibly undefined, but briefly,
      // when user removes one of the times.
      // When doing updates we are sure time is valid Meeting Pattern.
      const timeForSure = time as Ref<MeetingPattern>

      // Without following condition custom action will be triggered in loop.
      if (timeForSure.value[dataKey] === value) {
        return
      }

      if ([ 'startDate', 'endDate' ].includes(dataKey)) {
        updateStartAndEndDates()
      } else {
        update({ changes: { [dataKey]: value }, formValue: timeForSure })
      }

      // todo; when one of the dates is ref value, then the other will not be sanitized
      function updateStartAndEndDates () {
        const updatedMeetingPattern = { ...time.value, [dataKey]: value }
        // We only want to take sanitized values for dates
        // if the date value was previously defined, but is now out of range.
        // If the date value was not previously defined
        // (i.e. set as empty string or "null"),
        // we want to revert back to null state.
        // This is because the user is expecting the value
        // to inherit from the part of term or term date value.
        let {
          startDate = null,
          endDate = null,
        } = dateTimeUtils.getStartEndDatesSanitized(
          // TODO: Fix the keys here
          // @ts-expect-error
          time.value,
          updatedMeetingPattern,
        )

        if (!updatedMeetingPattern.startDate) {
          startDate = null
        }

        if (!updatedMeetingPattern.endDate) {
          endDate = null
        }

        root.$track(
          'Changed Meeting Pattern Start Date',
          { from: timeForSure.value.startDate, to: startDate },
        )
        root.$track(
          'Changed Meeting Pattern End Date',
          { from: timeForSure.value.endDate, to: endDate },
        )

        update({ changes: { startDate, endDate }, formValue: timeForSure })
      }
    }

    function update ({ changes, formValue }: {
      changes: Record<string, any>,
      formValue: Ref<MeetingPattern>,
    }): void {
      const valueWithChanges: MeetingPattern = {
        ...formValue.value,
        ...changes,
      }
      emit('input', valueWithChanges)
    }

    const questionsArr = computed(() => Object
      .entries(questions.value)
      .map(([ questionId, question ]) => mapQuestionConfig(question, questionId))
    )

    const template = computed(() => _chunk(questionsArr.value.map(q => q.questionId), 2))

    const { formId } = useDynamicForm({
      // @ts-expect-error
      questions: questionsArr,
      // TODO: Find a solution for template being any-length tupple
      // Type 'ComputedRef<string[][]>'
      // @ts-expect-error
      template,
      updateImpl: customFormUpdateImpl,
      formValue: section,
      path,
    })

    // @ts-expect-error
    // Expected 2-3 arguments, but got 0.
    // TODO: Vuelidate should have optional args notation
    const $v = useVuelidate()

    onMounted(async function () {
      // W/o double $nextTick, form won't be initially $dirty.
      // This is because the component is displayed in double modal.
      await root.$nextTick()
      await root.$nextTick()
      $v.value.$touch()
    })

    return {
      formId,
      meetingPatternLabel,
      $v,
    }
  },
})
